<template>
  <div>
    <FormUploadDropTarget @filesDropped="$refs.documentUpload.filesDroppedFromTarget($event)">
      <NotificationBar v-if="hasFormError()" type="error">
        <NotificationBarTitle>{{formErrorMessage()}}</NotificationBarTitle>
      </NotificationBar>
      <FormRow label="Contact Person Filter" v-show="contactListAvailable">
        <FormLabelTag 
          popupName="Contact Person"
          v-model="contactPersonLabelList"
          labelType="contactPerson"
          field="contactPersonLabelList"
          :editable="false" />
        <div class="form-field-note">Use the contact person filter to select the people to receive the email</div>
      </FormRow>
      <FormRowFull v-show="contactListAvailable">
        <SlimScroll :height="150">
          <StaticDataTable class="static-table-list" variant="table-sm">
            <template v-slot:header>
              <tr>
                <th>Person</th>
                <th>Email Address</th>
                <th>Labels</th>
                <th>Email</th>
              </tr>
            </template>
            <template v-slot:body>
              <tr v-for="emailContact in filteredPersonList" :key="emailContact.id">
                <td class="font-weight-bold">{{emailContact.firstName}} {{emailContact.lastName}}</td>
                <td>{{emailContact.email}}</td>
                <td><LabelTags :labelList="emailContact.labelList" /></td>
                <td>
                  <FormCheckbox class="p-0 m-0" v-show="emailContact.available" v-model="emailContact.emailFlag" @change="formUpdated" />
                  <span v-if="!emailContact.available" class="font-italic">Invalid email</span>
                </td>
              </tr>
            </template>
          </StaticDataTable>
          <Spinner v-if="contactEmailList.loading" />
        </SlimScroll>
      </FormRowFull>

      <FormHorizontalRule v-if="contactListAvailable" />
      <template v-if="showOptions">
        <slot name="options" />
      </template>
      <FormRow label="CC" v-show="ccAvailable">
        <template v-slot:label>
          <InlineHelpPopover title="CC Email">
            Enter addresses to CC the email to.<br />
            Note that some placeholders such as contact people will not be updated for the CC'd emails<br />
            Requires at least 1 contact to be selected for emailing
          </InlineHelpPopover>            
        </template>
        <template v-slot:default>
          <FormMultiSelect :taggable="true" placeholder="CC Email address list" v-model="ccList" @input="formUpdated" note="Press enter to add each email address to the list" field="ccList" />

          <div class="form-invalid-feedback" v-if="hasCCEmailError">
            <i class="fas fa-exclamation-triangle"></i> CC List contains an invalid email address
          </div>
        </template>
      </FormRow>
      <FormRow label="Email Template">
        <FormSelect :options="messageTemplateList" v-model="messageTemplateId" field="messageTemplateId"></FormSelect>
      </FormRow>
      <FormRow label="Email Subject">
        <FormInputText v-model="messageTemplate.subject" @input="formUpdated" v-show="!messageTemplate.loading" field="messageSubject" />
        <Spinner v-if="messageTemplate.loading" />
      </FormRow>
      <FormRow label="Email Content">
        <template v-slot:label>
          <InlineHelpPopover title="Template Help">
            Basic core placeholders are available for the email subject &amp; message, including:<br />
            [TradingName], [Contact.Name], [Contact.Person.FirstName], [Contact.Person.LastName], [Current.Date]
          </InlineHelpPopover>            
        </template>
        <template v-slot:default>
          <MessageTemplateEditor v-model="messageTemplate.message" @input="formUpdated" v-show="!messageTemplate.loading" field="messageContent" />
          <Spinner v-if="messageTemplate.loading" />
        </template>
      </FormRow>
      <FormRow label="Attached Document" v-if="previewLink && false">
        <FormStaticLabel>
          <a target="_blank" class="router-link-newtab" :href="previewLink">Preview <i class="far fa-external-link-alt ml-2"></i></a>
        </FormStaticLabel>
      </FormRow>
      <FormRow label="Attachments" v-if="attachmentsAvailable">
        <FormStaticLabel>
          <div class="email-preview-link mr-2" v-if="previewLink">
            <a target="_blank" :href="previewLink">Linked Document <i class="far fa-external-link-alt ml-2"></i></a>
          </div>
          <FormUpload ref="documentUpload" @change="uploadFileList = $event; formUpdated()" @uploading="$emit('uploading', $event)" field="uploadFileList" class="form-control-inline" />
        </FormStaticLabel>
      </FormRow>
    </FormUploadDropTarget>
  </div>
</template>

<style lang="scss" scoped>
.email-preview-link {
  display: inline-block;
  border: 1px solid #e7e7e7;
  background-color: #ffffff;
  color: #676a6c;
  height: $file-upload-height;
  line-height: $file-upload-height;
  vertical-align: middle;
  font-size: 0.7rem;
  border-radius: 3px;
  padding: 0 0.5rem;
}
</style>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import StaticDataTable from "@/components/table/staticDataTable.vue"
import SlimScroll from '@/components/slimScroll'
import MessageTemplateEditor from '@/modules/admin/components/messageTemplateEditor'

import { MessageTemplateTypes, CommunicationMethodTypes } from '@/store/types'

export default {
  name: 'EmailMessageForm',
  mixins: [
    form
  ],
  components: {
    StaticDataTable,
    SlimScroll,
    MessageTemplateEditor
  },
  props: {
    contactId: Number,
    defaultContactPersonId: Number,
    defaultCCList: Array,
    previewLink: String,
    contactListAvailable: {
      type: Boolean,
      default: true
    },
    ccAvailable: {
      type: Boolean,
      default: true
    },
    attachmentsAvailable: {
      type: Boolean,
      default: true
    },
    showOptions: {
      type: Boolean,
      default: false
    },
    requireContactPerson: {
      type: Boolean,
      default: true
    },
    templateType: String,
    messageTemplateUpdateFn: Function,
    contactListUpdateFn: Function
  },
  watch: {
    messageTemplateId: function(newVal, oldVal) {
      this.requestTemplateMessage()
    },
    contactPersonLabelList: function() {
      this.formUpdated()
    }
  },
  data() {
    return {
      responseInfo: {},
      emailForm: {},
      submitting: false,
      messageTemplateId: null,
      messageTemplateList: [],
      contactPersonLabelList: [],
      ccList: [],
      uploadFileList: [],

      contactEmailList: {
        loading: false,
        filteredList: [],
        list: []
      },
      messageTemplate: {
        loading: false,
        subject: '',
        message: ''
      }
    }
  },
  mounted() {
    this.$apiService.adminService.getMessageTemplateList()
    .then(response => {
      this.$data.messageTemplateList = response.data.filter(template => {
        return template.methodType == CommunicationMethodTypes.email && template.templateType == this.$props.templateType
      }).map(template => {
        return {
          value: template.id,
          name: template.name
        }
      })

      if(this.$data.messageTemplateList.length > 0) {
        this.$data.messageTemplateId = this.$data.messageTemplateList[0].value
      }
    })

    if(this.defaultCCList && this.defaultCCList.length > 0) {
      this.ccList = _.clone(this.defaultCCList)
      this.formUpdated()
    }

    this.requestEmailContactList()
  },
  computed: {
    emailEnabled() {
      if(!this.requireContactPerson && this.ccList && this.ccList.length > 0)
        return true
      return this.filteredPersonList.filter(person => person.emailFlag).length > 0
    },
    filteredPersonList() {
      if(this.contactPersonLabelList.length == 0)
        return this.contactEmailList.list

      return this.contactEmailList.list.filter(person => {
        return this.contactPersonLabelList.filter(label => {
          return person.labelList.find(personLabel => personLabel.id == label.id)
        }).length > 0
      })
    },
    hasCCAddresses() {
      return this.ccList && this.ccList.length > 0
    },
    hasCCEmailError() {
      if(!this.hasCCAddresses)
        return false

      // Check if any of the CC emails are invalid
      return this.ccList.some(email => {
        if(email == null || email == '' || (email + '').trim() == '')
          return false

        return !this.isValidEmail(email)
      })
    }
  },
  methods: {
    requestEmailContactList() {
      // Use the contact list function if provided instead of doing a regular contact lookup
      // Used for some forms that may email multiple contacts
      if(this.$props.contactListUpdateFn != null) {
        this.contactEmailList.loading = true
        this.contactEmailList.list = []

        this.$props.contactListUpdateFn()
        .then(contactPersonList => {
          this.contactEmailList.list = contactPersonList.map(person => {
            return {
              id: person.id,
              firstName: person.firstName,
              lastName: person.lastName,
              email: person.email,
              labelList: person.labelList,
              emailFlag: this.isValidEmail(person.email),
              available: this.isValidEmail(person.email),
              contact: person.contact,
            }
          })

          this.formUpdated()
        })
        .catch(err => {
          this.$data.emailForm = this.processFormResponse(err)
        })
        .finally(() => {
          this.contactEmailList.loading = false
        })
        return
      }
      if(this.contactId == null)
        return

      this.contactEmailList.loading = true
      this.contactEmailList.list = []

      this.$apiService.contactService.getContact(this.contactId)
      .then(response => {
        this.contactEmailList.list = response.data.persons.map(person => {
          let emailFlag = this.isValidEmail(person.email)

          // If default contact person is set, use that instead
          if(this.defaultContactPersonId)
            emailFlag = person.id == this.defaultContactPersonId

          // If no default contact person is set & we have a CC list, default to none
          if(!this.defaultContactPersonId && this.hasCCAddresses)
            emailFlag = false

          return {
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            email: person.email,
            labelList: person.labelList,
            emailFlag: emailFlag,
            available: this.isValidEmail(person.email),
          }
        })

        this.formUpdated()
      })
      .catch(err => {
        this.$data.emailForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.contactEmailList.loading = false
      })
    },

    requestTemplateMessage() {
      if(this.$props.messageTemplateUpdateFn == null)
        return

      this.messageTemplate.loading = true
      this.messageTemplate.subject = ''
      this.messageTemplate.message = ''

      this.$props.messageTemplateUpdateFn(this.$data.messageTemplateId, this.$props.contactId)
      .then(data => {
        this.messageTemplate.subject = data.templateSubject
        this.messageTemplate.message = data.templateContent

        this.formUpdated()
      })
      .catch(err => {
        this.$data.emailForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.messageTemplate.loading = false
      })
    },
    hasFormError() {
      return (this.emailForm != undefined && this.emailForm.errorMessage != undefined)
    },
    formErrorMessage() {
      if(this.emailForm != undefined && this.emailForm.errorMessage != undefined)
        return this.emailForm.errorMessage
      return ''
    },
    formUpdated() {
      let formData = {
        messageSubject: this.messageTemplate.subject,
        messageContent: this.messageTemplate.message,
        contactPersonList: this.filteredPersonList.filter(person => person.emailFlag),
        uploadFileList: this.uploadFileList,
        ccList: this.ccList
      }

      this.$emit('update', formData)
    }
  }
}
</script>